import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { query, collection, where, getDocs, updateDoc, doc, deleteDoc, getDoc } from 'firebase/firestore';

// Oiltankportal (Clients, Realtors, Attorneys)
const portalFirebaseConfig = {
  apiKey: "AIzaSyDUpQKBAR75onJjWc_-i4BxvDnzmQ-W9Uc",
  authDomain: "oiltankportal.firebaseapp.com",
  projectId: "oiltankportal",
  storageBucket: "oiltankportal.appspot.com",
  messagingSenderId: "101379795965",
  appId: "1:101379795965:web:80eb8987cd47a5bf164794"
};

// Initialize Portal Firebase app
const portalApp = initializeApp(portalFirebaseConfig, "portalApp");
export const portalAuth = getAuth(portalApp);
export const portalDb = getFirestore(portalApp);
export const portalStorage = getStorage(portalApp);

// Oiltanksweep (Reports Database, Storage)
const sweepFirebaseConfig = {
  apiKey: "AIzaSyBueBbCMNqgdQykN6w4aOYoZci3EwTfci8",
  authDomain: "oiltanksweep.firebaseapp.com",
  projectId: "oiltanksweep",
  storageBucket: "oiltanksweep.appspot.com",
  messagingSenderId: "856028194739",
  appId: "1:856028194739:web:c90ae62228ef60f149cdc6"
};

// Initialize Sweep Firebase app
const sweepApp = initializeApp(sweepFirebaseConfig, "sweepApp");
export const sweepDb = getFirestore(sweepApp);
export const sweepStorage = getStorage(sweepApp);

// Export default auth and db references (for convenience)
export const auth = portalAuth;
export const db = sweepDb; // We're primarily reading from the sweep database

// Function to handle cascading deletion of reports
// This should be called when an inspector deletes a report
export const deleteReportWithCascade = async (reportId) => {
  try {
    // 1. Get all users who have this report saved
    const usersWithReportQuery = query(
      collection(portalDb, 'users'),
      where('savedReports', 'array-contains', reportId)
    );
    
    const usersWithReport = await getDocs(usersWithReportQuery);
    
    // 2. Delete the report from each user's savedReports array
    const userUpdatePromises = usersWithReport.docs.map(async (userDoc) => {
      const userData = userDoc.data();
      const updatedReports = (userData.savedReports || []).filter(id => id !== reportId);
      
      return updateDoc(doc(portalDb, 'users', userDoc.id), {
        savedReports: updatedReports
      });
    });
    
    // 3. Delete any notes associated with this report
    const reportNotesQuery = query(
      collection(portalDb, 'reportNotes'),
      where('reportId', '==', reportId)
    );
    
    const reportNotes = await getDocs(reportNotesQuery);
    
    const noteDeletePromises = reportNotes.docs.map(noteDoc => 
      deleteDoc(doc(portalDb, 'reportNotes', noteDoc.id))
    );
    
    // 4. Wait for all updates to complete
    await Promise.all([...userUpdatePromises, ...noteDeletePromises]);
    
    // 5. Finally delete the actual report
    await deleteDoc(doc(sweepDb, 'inspections', reportId));
    
    console.log(`Report ${reportId} deleted with all references cleaned up`);
    return true;
  } catch (error) {
    console.error('Error in cascading report deletion:', error);
    throw error;
  }
};

// Function to add a notification to a user's account
export const addNotificationToUser = async (userId, notification) => {
  try {
    const userRef = doc(portalDb, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // Get current notifications or initialize empty array
      const currentNotifications = userDoc.data().notifications || [];
      
      // Add new notification with timestamp
      const notificationWithTimestamp = {
        ...notification,
        timestamp: new Date().toISOString(),
        read: false,
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      
      // Update user document with new notifications array
      // Add to beginning of array so newest are first
      await updateDoc(userRef, {
        notifications: [notificationWithTimestamp, ...currentNotifications]
      });
      
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error adding notification:', error);
    return false;
  }
};

// Function to notify clients when a report is deleted
export const notifyClientsOfReportDeletion = async (reportId, reportData) => {
  try {
    // Find all users who have this report
    const usersWithReportQuery = query(
      collection(portalDb, 'users'),
      where('savedReports', 'array-contains', reportId)
    );
    
    const usersWithReport = await getDocs(usersWithReportQuery);
    
    // Create notifications for all affected users
    const notificationPromises = usersWithReport.docs.map(userDoc => {
      const notification = {
        type: 'report_deleted',
        title: 'Report No Longer Available',
        message: `The report for "${reportData.propertyAddress || 'Unknown Property'}" is no longer available.`,
        reportId: reportId,
        propertyAddress: reportData.propertyAddress || 'Unknown Property',
        clientName: reportData.clientName || 'Unknown Client'
      };
      
      return addNotificationToUser(userDoc.id, notification);
    });
    
    await Promise.all(notificationPromises);
    return true;
  } catch (error) {
    console.error('Error notifying users of report deletion:', error);
    return false;
  }
};

// Function for inspectors to archive a report instead of deleting it completely
export const archiveReport = async (reportId, archiveReason = 'No reason provided') => {
  try {
    const reportRef = doc(sweepDb, 'inspections', reportId);
    const reportDoc = await getDoc(reportRef);
    
    if (!reportDoc.exists()) {
      throw new Error(`Report ${reportId} not found`);
    }
    
    const reportData = reportDoc.data();
    
    // Mark the report as deleted/archived
    await updateDoc(reportRef, {
      isDeleted: true,
      deletedAt: new Date().toISOString(),
      archiveReason: archiveReason,
      // Keep important metadata intact for reference
      originalData: {
        clientName: reportData.clientName,
        propertyAddress: reportData.propertyAddress,
        dateOfInspection: reportData.dateOfInspection,
        scenario: reportData.scenario
      }
    });
    
    // Notify clients who have this report saved
    await notifyClientsOfReportDeletion(reportId, reportData);
    
    console.log(`Report ${reportId} archived successfully`);
    return true;
  } catch (error) {
    console.error('Error archiving report:', error);
    throw error;
  }
};

// Function to notify users when a report is added to their account
export const notifyReportAdded = async (userId, reportId, reportData) => {
  try {
    // Check if the user already has this report in their notifications
    const userRef = doc(portalDb, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.error(`User ${userId} not found`);
      return false;
    }
    
    const userData = userDoc.data();
    const notifications = userData.notifications || [];
    
    // Check recent notifications for this report (within last 24 hours)
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
    
    // Look for any recent notifications about this report
    const hasRecentNotification = notifications.some(notif => {
      // Match by reportId
      if (notif.reportId === reportId) {
        // Check timestamp (if it exists)
        if (notif.timestamp) {
          const notifTime = new Date(notif.timestamp);
          return notifTime > oneDayAgo;
        }
        return true; // If no timestamp, assume it's recent
      }
      
      // Also check if there's a notification with the same property address
      // This prevents duplicates when the same property gets re-reported
      if (notif.propertyAddress && reportData.propertyAddress && 
          notif.propertyAddress.toLowerCase() === reportData.propertyAddress.toLowerCase()) {
        if (notif.timestamp) {
          const notifTime = new Date(notif.timestamp);
          return notifTime > oneDayAgo;
        }
        return true;
      }
      
      return false;
    });
    
    // If there's already a notification for this report or address recently, don't add another one
    if (hasRecentNotification) {
      console.log(`Skip notification for report ${reportId} - already notified within 24 hours`);
      return true; // Return success but don't add duplicate notification
    }
    
    // If no existing notification, create and add one
    const notification = {
      type: 'report_added',
      title: 'New Report Added',
      message: `A new report for "${reportData.propertyAddress || 'Unknown Property'}" has been added to your account.`,
      reportId: reportId,
      propertyAddress: reportData.propertyAddress || 'Unknown Property',
      clientName: reportData.clientName || 'Unknown Client'
    };
    
    return await addNotificationToUser(userId, notification);
  } catch (error) {
    console.error('Error notifying user of report addition:', error);
    return false;
  }
};

// Combine the archive and deletion functions for a complete solution
export const deleteOrArchiveReport = async (reportId, shouldArchive = true, archiveReason = '') => {
  try {
    // Get report data before it's potentially deleted
    const reportRef = doc(sweepDb, 'inspections', reportId);
    const reportDoc = await getDoc(reportRef);
    
    if (!reportDoc.exists()) {
      throw new Error(`Report ${reportId} not found`);
    }
    
    const reportData = reportDoc.data();
    
    if (shouldArchive) {
      // Soft delete (archive)
      await archiveReport(reportId, archiveReason);
    } else {
      // Hard delete with cascade
      // First notify users
      await notifyClientsOfReportDeletion(reportId, reportData);
      // Then perform cascading delete
      await deleteReportWithCascade(reportId);
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteOrArchiveReport:', error);
    throw error;
  }
};

// Function to check report status in main app
export const checkAndHandleReportStatus = async (reportId, userId) => {
  try {
    // Get report from main database
    const reportRef = doc(sweepDb, 'inspections', reportId);
    const reportDoc = await getDoc(reportRef);
    
    if (!reportDoc.exists()) {
      console.log(`Report ${reportId} not found in main database`);
      
      // Report doesn't exist at all - notify user if needed
      if (userId) {
        // Check if we've already notified this user about this report
        const userRef = doc(portalDb, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const notifications = userData.notifications || [];
          
          // Check if there's already a notification about this report being deleted
          const alreadyNotified = notifications.some(
            notif => notif.type === 'report_deleted' && notif.reportId === reportId
          );
          
          if (!alreadyNotified) {
            // Notify user about deleted report
            await addNotificationToUser(userId, {
              type: 'report_deleted',
              title: 'Report No Longer Available',
              message: `The report is no longer available in the system. It may have been permanently deleted by the inspector.`,
              reportId: reportId
            });
          }
        }
      }
      
      // Maintain backward compatibility with original return structure
      return { 
        status: 'not_found',
        error: 'Report not found',
        // New fields for our payment feature
        exists: false,
        isPaid: false
      };
    }
    
    // Report exists, check its status
    const reportData = reportDoc.data();
    const reportStatus = reportData.status || 'active';
    
    // Check payment status - default to true for backward compatibility
    const isPaid = reportData.isPaid !== false;
    
    // Backward compatibility with original return structure
    return { 
      status: reportStatus === 'removed' || reportStatus === 'trashed' ? 'deleted' : 'active',
      reportData: reportData,
      // New fields for our payment feature
      exists: true,
      isPaid: isPaid,
      report: reportData
    };
  } catch (error) {
    console.error('Error checking report status:', error);
    return { 
      status: 'error', 
      error: error.message,
      // New fields for our payment feature
      exists: false,
      isPaid: false
    };
  }
};

// Batch process to check all reports for a user and handle removed ones
export const syncUserReportsWithMainApp = async (userId) => {
  try {
    // Get user's saved reports
    const userRef = doc(portalDb, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const userData = userDoc.data();
    const savedReports = userData.savedReports || [];
    
    if (savedReports.length === 0) {
      return { success: true, message: 'No reports to sync' };
    }
    
    // Check status of each report
    const reportStatusPromises = savedReports.map(reportId => 
      checkAndHandleReportStatus(reportId, userId)
    );
    
    const reportStatuses = await Promise.all(reportStatusPromises);
    
    // Count missing reports
    const missingReports = reportStatuses.filter(rs => rs.status === 'not_found');
    
    if (missingReports.length > 0) {
      console.log(`Found ${missingReports.length} missing reports for user ${userId}`);
    }
    
    return { 
      success: true, 
      totalReports: savedReports.length,
      missingReports: missingReports.length
    };
  } catch (error) {
    console.error('Error syncing reports:', error);
    return { success: false, error: error.message };
  }
};